import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  CircularProgress,
  Alert,
  Grid,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Link as LinkIcon,
  Computer as ComputerIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { useAuth } from '../../contexts/AuthContext';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

const workTypes = [
  { value: 'passport', label: 'Passport' },
  { value: 'pan_card', label: 'PAN Card' },
  { value: 'aadhaar', label: 'Aadhaar' },
  { value: 'bills', label: 'Bills' },
  { value: 'coupons', label: 'Coupons' },
  { value: 'job_alerts', label: 'Job Alerts' },
  { value: 'other', label: 'Other' },
];

const validationSchema = yup.object({
  work_type: yup.string().required('Work type is required'),
  work_name: yup.string().required('Work name is required'),
  link_url: yup.string().url('Invalid URL').required('Link URL is required'),
  description: yup.string(),
  icon_url: yup.string().url('Invalid URL'),
  display_order: yup.number().min(0, 'Display order must be >= 0'),
});

const OnlineWorksManagement = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(null);
  const [editingWork, setEditingWork] = useState(null);

  const { data, isLoading, error } = useQuery(
    'online-works',
    async () => {
      const response = await axios.get(`${API_BASE_URL}/online-works`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      return response.data.works || [];
    }
  );

  const createMutation = useMutation(
    async (values) => {
      const response = await axios.post(`${API_BASE_URL}/online-works`, values, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      return response.data;
    },
    {
      onSuccess: () => {
        toast.success('Online work created successfully');
        queryClient.invalidateQueries('online-works');
        formik.resetForm();
        setDialogOpen(false);
        setEditingWork(null);
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to create online work');
      },
    }
  );

  const updateMutation = useMutation(
    async ({ id, ...values }) => {
      const response = await axios.put(`${API_BASE_URL}/online-works/${id}`, values, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      return response.data;
    },
    {
      onSuccess: () => {
        toast.success('Online work updated successfully');
        queryClient.invalidateQueries('online-works');
        formik.resetForm();
        setDialogOpen(false);
        setEditingWork(null);
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to update online work');
      },
    }
  );

  const deleteMutation = useMutation(
    async (id) => {
      const response = await axios.delete(`${API_BASE_URL}/online-works/${id}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      return response.data;
    },
    {
      onSuccess: () => {
        toast.success('Online work deleted successfully');
        queryClient.invalidateQueries('online-works');
        setDeleteDialog(null);
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to delete online work');
      },
    }
  );

  const formik = useFormik({
    initialValues: {
      work_type: '',
      work_name: '',
      description: '',
      link_url: '',
      icon_url: '',
      is_active: true,
      display_order: 0,
    },
    validationSchema: validationSchema,
    onSubmit: (values) => {
      if (editingWork) {
        updateMutation.mutate({ id: editingWork.id, ...values });
      } else {
        createMutation.mutate(values);
      }
    },
    enableReinitialize: true,
  });

  const handleEdit = (work) => {
    setEditingWork(work);
    formik.setValues({
      work_type: work.work_type || '',
      work_name: work.work_name || '',
      description: work.description || '',
      link_url: work.link_url || '',
      icon_url: work.icon_url || '',
      is_active: work.is_active !== undefined ? work.is_active : true,
      display_order: work.display_order || 0,
    });
    setDialogOpen(true);
  };

  const handleAdd = () => {
    setEditingWork(null);
    formik.resetForm();
    setDialogOpen(true);
  };

  const getWorkTypeColor = (type) => {
    const colors = {
      passport: 'primary',
      pan_card: 'success',
      aadhaar: 'info',
      bills: 'warning',
      coupons: 'secondary',
      job_alerts: 'error',
      other: 'default',
    };
    return colors[type] || 'default';
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">Failed to load online works. Please try again.</Alert>;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" fontWeight="bold">
          Online Works Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAdd}
          disabled={!['app_admin', 'super_admin', 'net_center_admin'].includes(user?.role)}
        >
          Add Online Work
        </Button>
      </Box>

      <Card>
        <CardContent>
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Work Name</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Link</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Order</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Center</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data?.map((work) => (
                  <TableRow key={work.id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ComputerIcon color="primary" />
                        <Typography fontWeight="medium">{work.work_name}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={work.work_type.replace('_', ' ')}
                        color={getWorkTypeColor(work.work_type)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Button
                        size="small"
                        href={work.link_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        startIcon={<LinkIcon />}
                      >
                        Open Link
                      </Button>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {work.description || 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell>{work.display_order}</TableCell>
                    <TableCell>
                      <Chip
                        label={work.is_active ? 'Active' : 'Inactive'}
                        color={work.is_active ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{work.center_name || 'N/A'}</TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => handleEdit(work)}
                        disabled={!['app_admin', 'super_admin', 'net_center_admin'].includes(user?.role)}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => setDeleteDialog(work)}
                        disabled={!['app_admin', 'super_admin', 'net_center_admin'].includes(user?.role)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {data?.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <Typography variant="body2" color="text.secondary">
                        No online works found
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <form onSubmit={formik.handleSubmit}>
          <DialogTitle>
            {editingWork ? 'Edit Online Work' : 'Add Online Work'}
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Work Type</InputLabel>
                  <Select
                    id="work_type"
                    name="work_type"
                    value={formik.values.work_type}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.work_type && Boolean(formik.errors.work_type)}
                  >
                    {workTypes.map((type) => (
                      <MenuItem key={type.value} value={type.value}>
                        {type.label}
                      </MenuItem>
                    ))}
                  </Select>
                  {formik.touched.work_type && formik.errors.work_type && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                      {formik.errors.work_type}
                    </Typography>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  id="work_name"
                  name="work_name"
                  label="Work Name"
                  value={formik.values.work_name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.work_name && Boolean(formik.errors.work_name)}
                  helperText={formik.touched.work_name && formik.errors.work_name}
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  id="link_url"
                  name="link_url"
                  label="Link URL"
                  value={formik.values.link_url}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.link_url && Boolean(formik.errors.link_url)}
                  helperText={formik.touched.link_url && formik.errors.link_url}
                  margin="normal"
                  placeholder="https://example.com"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  id="description"
                  name="description"
                  label="Description"
                  multiline
                  rows={3}
                  value={formik.values.description}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.description && Boolean(formik.errors.description)}
                  helperText={formik.touched.description && formik.errors.description}
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  id="icon_url"
                  name="icon_url"
                  label="Icon URL (Optional)"
                  value={formik.values.icon_url}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.icon_url && Boolean(formik.errors.icon_url)}
                  helperText={formik.touched.icon_url && formik.errors.icon_url}
                  margin="normal"
                  placeholder="https://example.com/icon.png"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  id="display_order"
                  name="display_order"
                  label="Display Order"
                  type="number"
                  value={formik.values.display_order}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.display_order && Boolean(formik.errors.display_order)}
                  helperText={formik.touched.display_order && formik.errors.display_order}
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      id="is_active"
                      name="is_active"
                      checked={formik.values.is_active}
                      onChange={formik.handleChange}
                    />
                  }
                  label="Active"
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button
              type="submit"
              variant="contained"
              disabled={createMutation.isLoading || updateMutation.isLoading}
            >
              {createMutation.isLoading || updateMutation.isLoading ? (
                <CircularProgress size={24} />
              ) : editingWork ? (
                'Update'
              ) : (
                'Create'
              )}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={!!deleteDialog} onClose={() => setDeleteDialog(null)}>
        <DialogTitle>Delete Online Work</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{deleteDialog?.work_name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(null)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => deleteMutation.mutate(deleteDialog.id)}
            disabled={deleteMutation.isLoading}
          >
            {deleteMutation.isLoading ? <CircularProgress size={24} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OnlineWorksManagement;
